/** @file Layers a Sample's continuous channels onto a Pose via the blend kernel: bones, colours, IK, deform. */
import { clamp } from '@pose2d/math/clamp';
import { color } from '@pose2d/math/color';
import { POSE_LAYOUT as L, type Pose } from '../contracts/pose';
import type { Sample } from '../contracts/sample';
import type { SkeletonData } from '../skeletonData';
import { sampleIndex } from '../_internal/timeline/sampleIndex';
import { blendChannel } from './blendChannel';
import { channelClass, type ChannelClass } from './channelClass';

type Blend = 'replace' | 'add';

const light: [number, number, number, number] = [0, 0, 0, 0];
const dark: [number, number, number, number] = [0, 0, 0, 0];
/** Deform offsets blend as additive channels with setup 0 (replace lerps, add adds alpha × offset). */
const DEFORM_CLASS: ChannelClass = 1;

/** Bone channels: Sample.values index c is also the Pose.local index (the bone block starts at 0). */
function blendBones(pose: Pose, data: SkeletonData, sample: Sample, alpha: number, blend: Blend, slotStart: number, ikStart: number): void {
  for (let c = 0; c < slotStart; c++) {
    if (sample.mask[c] !== 1) continue;
    const setup = data.bones[Math.floor(c / L.LOCAL_STRIDE)]?.setup[c % L.LOCAL_STRIDE] ?? 0;
    pose.local[c] = blendChannel(channelClass(c, slotStart, ikStart), pose.local[c] ?? 0, sample.values[c] ?? 0, setup, alpha, blend);
  }
}

/** One colour byte moved toward keyed float channel i through the kernel and written back rounded (the only rounding). */
function mixByte(byte: number, sample: Sample, i: number, cls: ChannelClass, alpha: number, blend: Blend): number {
  if (sample.mask[i] !== 1) return byte;
  return Math.round(clamp(blendChannel(cls, byte / 255, sample.values[i] ?? 0, 0, alpha, blend), 0, 1) * 255);
}

/** Slot colours: keyed float channels into the packed light and dark words; the dark A byte is kept. */
function blendSlots(pose: Pose, data: SkeletonData, sample: Sample, alpha: number, blend: Blend, slotStart: number, ikStart: number): void {
  for (let s = 0; s < data.slots.length; s++) {
    const o = slotStart + s * L.SLOT_STRIDE;
    let keyed = false;
    for (let c = 0; c < L.SLOT_STRIDE; c++) keyed = keyed || sample.mask[o + c] === 1;
    if (!keyed) continue;
    const cls = channelClass(o, slotStart, ikStart);
    color.unpack(pose.slotColor[s] ?? 0, light);
    color.unpack(pose.slotDark[s] ?? 0, dark);
    pose.slotColor[s] = color.pack(
      mixByte(light[0], sample, o + L.SLOT_R, cls, alpha, blend),
      mixByte(light[1], sample, o + L.SLOT_G, cls, alpha, blend),
      mixByte(light[2], sample, o + L.SLOT_B, cls, alpha, blend),
      mixByte(light[3], sample, o + L.SLOT_ALPHA, cls, alpha, blend),
    );
    pose.slotDark[s] = color.pack(
      mixByte(dark[0], sample, o + L.SLOT_DARK_R, cls, alpha, blend),
      mixByte(dark[1], sample, o + L.SLOT_DARK_G, cls, alpha, blend),
      mixByte(dark[2], sample, o + L.SLOT_DARK_B, cls, alpha, blend),
      dark[3],
    );
  }
}

/** IK channels: Sample index c is Pose.ik[c - ikStart]; mix/softness blend, the flags (class 4) are applyDiscreteChannels'. */
function blendIk(pose: Pose, sample: Sample, alpha: number, blend: Blend, slotStart: number, ikStart: number): void {
  for (let c = ikStart; c < sample.values.length; c++) {
    if (sample.mask[c] !== 1) continue;
    const cls = channelClass(c, slotStart, ikStart);
    if (cls === 4) continue;
    const p = c - ikStart;
    pose.ik[p] = blendChannel(cls, pose.ik[p] ?? 0, sample.values[c] ?? 0, 0, alpha, blend);
  }
}

/** Deform offsets of every keyed attachment buffer, matched by attachment index (W2-03). */
function blendDeform(pose: Pose, sample: Sample, alpha: number, blend: Blend): void {
  for (let a = 0; a < sample.deformKeyed.length; a++) {
    const src = sample.deform[a];
    const dst = pose.deform[a];
    if (sample.deformKeyed[a] !== 1 || src === undefined || dst === undefined) continue;
    const n = Math.min(src.length, dst.length);
    for (let j = 0; j < n; j++) dst[j] = blendChannel(DEFORM_CLASS, dst[j] ?? 0, src[j] ?? 0, 0, alpha, blend);
  }
}

/** Layers sample's keyed continuous channels onto pose via channelClass + blendChannel; discrete ones are applyDiscreteChannels'. */
export function blendSample(pose: Pose, data: SkeletonData, sample: Sample, alpha: number, blend: Blend): void {
  const slotStart = sampleIndex(data, 'slot', 0, 0);
  const ikStart = sampleIndex(data, 'ik', 0, 0);
  blendBones(pose, data, sample, alpha, blend, slotStart, ikStart);
  blendSlots(pose, data, sample, alpha, blend, slotStart, ikStart);
  blendIk(pose, sample, alpha, blend, slotStart, ikStart);
  blendDeform(pose, sample, alpha, blend);
}
